import type {
  CalendarDateRange,
  CalendarDateRangeBounds,
  CalendarDateRangePreset,
  CalendarDateToken,
} from 'src/modules/shared/types/date-range.type';

/** Machine-readable validation failures for a calendar date range. */
export const CalendarDateRangeIssue = {
  MISSING: 'missing',
  INVALID: 'invalid',
  ORDER: 'order',
  BEFORE_MIN: 'before-min',
  AFTER_MAX: 'after-max',
} as const;

/** A possible calendar date-range validation failure. */
export type CalendarDateRangeIssueCode =
  (typeof CalendarDateRangeIssue)[keyof typeof CalendarDateRangeIssue];

const CALENDAR_TOKEN_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parses an ISO calendar token without applying a UTC offset.
 *
 * Noon is used so calendar rendering remains stable around daylight-saving
 * transitions.
 */
export const parseCalendarDateToken = (
  token: CalendarDateToken | undefined
): Date | null => {
  if (!token) {
    return null;
  }

  const match = CALENDAR_TOKEN_PATTERN.exec(token);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day, 12);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
};

/** Converts a local `Date` into an ISO calendar token. */
export const toCalendarDateToken = (date: Date): CalendarDateToken => {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/** Returns the first validation issue for a calendar date range. */
export const getCalendarDateRangeIssue = (
  value: CalendarDateRange,
  bounds: CalendarDateRangeBounds = {}
): CalendarDateRangeIssueCode | null => {
  if (!value.from || !value.to) {
    return CalendarDateRangeIssue.MISSING;
  }

  if (
    !parseCalendarDateToken(value.from) ||
    !parseCalendarDateToken(value.to)
  ) {
    return CalendarDateRangeIssue.INVALID;
  }

  if (value.from > value.to) {
    return CalendarDateRangeIssue.ORDER;
  }

  if (bounds.minDate && value.from < bounds.minDate) {
    return CalendarDateRangeIssue.BEFORE_MIN;
  }

  if (bounds.maxDate && value.to > bounds.maxDate) {
    return CalendarDateRangeIssue.AFTER_MAX;
  }

  return null;
};

/** Returns whether two possibly incomplete ranges contain the same tokens. */
export const areCalendarDateRangesEqual = (
  left: CalendarDateRange,
  right: CalendarDateRange
): boolean => left.from === right.from && left.to === right.to;

/** Finds the first shortcut whose concrete dates equal the supplied range. */
export const findMatchingCalendarDateRangePreset = <TId extends string>(
  value: CalendarDateRange,
  presets: CalendarDateRangePreset<TId>[]
): CalendarDateRangePreset<TId> | null =>
  presets.find((preset) => areCalendarDateRangesEqual(preset.value, value)) ??
  null;

/** Formats a complete range for a compact trigger label. */
export const formatCalendarDateRange = (
  value: CalendarDateRange,
  locale: string
): string => {
  const from = parseCalendarDateToken(value.from);
  const to = parseCalendarDateToken(value.to);

  if (!from || !to) {
    return '';
  }

  const formatter = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: from.getFullYear() === to.getFullYear() ? undefined : 'numeric',
  });
  const toFormatter = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return `${formatter.format(from)} – ${toFormatter.format(to)}`;
};
